import { failure, validFoodEntry } from '../domain/nutrition.ts';
import type { CompositionSnapshot, FoodEntry, NutritionResult, NutrientSnapshot } from '../domain/nutrition.ts';
import type { NutritionIntent } from './nutrition-intent.ts';

// Form parsing is shared by planning, actual consumption, recipes and shopping.
// Missing text is absence; malformed text is an error, never an implicit zero.
export function parseOptionalQuantity(raw: string | null | undefined): NutritionResult<number | undefined> {
  if (raw == null) return { ok: true, value: undefined };
  if (typeof raw !== 'string') return failure('VALIDATION_ERROR');
  if (raw.trim() === '') return { ok: true, value: undefined };
  const text = raw.trim().replace(',', '.');
  if (!/^[+-]?(?:\d+(?:\.\d*)?|\.\d+)$/.test(text)) return failure('VALIDATION_ERROR');
  const value = Number(text);
  return Number.isFinite(value) && value >= 0 ? { ok: true, value } : failure('VALIDATION_ERROR');
}

export interface CompositionDraft {
  sourceLabel?: string;
  observedAt?: string;
  externalReference?: string;
  versionOrEdition?: string;
  referenceAmount?: string;
  referenceUnit?: string;
  preparationBase?: string;
  energyKcal?: string;
  proteinG?: string;
  carbohydrateG?: string;
  fatG?: string;
}

export interface FoodDraft {
  name: string;
  value?: string;
  unit?: string;
  basis?: string;
  equivalenceGrams?: string;
  equivalenceContext?: string;
  composition?: CompositionDraft;
}

function nutrientFromForm(raw: string | undefined): NutritionResult<NutrientSnapshot> {
  const parsed = parseOptionalQuantity(raw);
  if (!parsed.ok) return parsed;
  return parsed.value === undefined
    ? { ok: true, value: { state: 'UNKNOWN' } }
    : { ok: true, value: { state: 'KNOWN', value: parsed.value } };
}

function compositionFromForm(draft: CompositionDraft): NutritionResult<CompositionSnapshot> {
  const values = Object.values(draft).map(value => value?.trim() ?? '');
  if (values.every(value => value === '')) return { ok: true, value: { state: 'UNKNOWN' } };

  const label = draft.sourceLabel?.trim() ?? '';
  const observedAt = draft.observedAt?.trim() ?? '';
  const referenceUnit = draft.referenceUnit?.trim() ?? '';
  const preparationBase = draft.preparationBase?.trim() ?? '';
  const referenceAmount = parseOptionalQuantity(draft.referenceAmount);
  if (!referenceAmount.ok || referenceAmount.value === undefined || referenceAmount.value <= 0
    || !label || !observedAt || !referenceUnit || !preparationBase) return failure('VALIDATION_ERROR');

  const energyKcal = nutrientFromForm(draft.energyKcal);
  const proteinG = nutrientFromForm(draft.proteinG);
  const carbohydrateG = nutrientFromForm(draft.carbohydrateG);
  const fatG = nutrientFromForm(draft.fatG);
  if (!energyKcal.ok) return energyKcal;
  if (!proteinG.ok) return proteinG;
  if (!carbohydrateG.ok) return carbohydrateG;
  if (!fatG.ok) return fatG;

  return {
    ok: true,
    value: {
      state: 'KNOWN',
      source: {
        label,
        observedAt,
        ...(draft.externalReference?.trim() ? { externalReference: draft.externalReference.trim() } : {}),
        ...(draft.versionOrEdition?.trim() ? { versionOrEdition: draft.versionOrEdition.trim() } : {}),
      },
      reference: {
        amount: referenceAmount.value,
        unit: referenceUnit,
        preparationBase,
      },
      nutrients: {
        energyKcal: energyKcal.value,
        proteinG: proteinG.value,
        carbohydrateG: carbohydrateG.value,
        fatG: fatG.value,
      },
    },
  };
}

// Hydrate from the exact observed revision, including fields this form does not
// expose. Never reload/merge a newer revision here: expectedRevision owns that check.
export function foodEntriesForForm(observed: readonly FoodEntry[], draft: FoodDraft): NutritionResult<readonly FoodEntry[]> {
  const entries = structuredClone(observed) as FoodEntry[];
  const original = entries[0];
  const name = draft.name === original?.name ? original.name : draft.name.trim();
  const extraDraft = !!(
    draft.equivalenceGrams?.trim()
    || draft.equivalenceContext?.trim()
    || (draft.composition && Object.values(draft.composition).some(value => value?.trim()))
  );
  if (!name) {
    if (draft.value?.trim() || draft.unit?.trim() || draft.basis?.trim() || extraDraft) return failure('VALIDATION_ERROR');
    return { ok: true, value: original ? entries.slice(1) : [] };
  }

  const first: FoodEntry = original ? { ...original, name } : { name };
  if (draft.value !== undefined) {
    const parsed = parseOptionalQuantity(draft.value);
    if (!parsed.ok) return parsed;
    const unit = draft.unit?.trim() ?? '';
    const basis = draft.basis?.trim() ?? '';
    const previous = original?.quantity;
    const equivalenceWasExposed = draft.equivalenceGrams !== undefined || draft.equivalenceContext !== undefined;
    let equivalence = previous?.equivalence;

    if (equivalenceWasExposed) {
      const grams = parseOptionalQuantity(draft.equivalenceGrams);
      if (!grams.ok) return grams;
      const context = draft.equivalenceContext?.trim() ?? '';
      const hasGrams = grams.value !== undefined;
      const hasContext = context !== '';
      if (hasGrams !== hasContext || (hasGrams && (grams.value! <= 0 || parsed.value === undefined))) return failure('VALIDATION_ERROR');
      equivalence = hasGrams ? { grams: grams.value!, context } : undefined;
    }

    const previousEquivalence = previous?.equivalence;
    const sameEquivalence = equivalence?.grams === previousEquivalence?.grams
      && equivalence?.context === previousEquivalence?.context;
    const unchanged = (draft.value === previous?.value.toString() && draft.unit === previous.unit
      && (draft.basis ?? '') === (previous.basis ?? '') && sameEquivalence)
      || (parsed.value === previous?.value && unit === (previous?.unit ?? '')
        && basis === (previous?.basis ?? '') && sameEquivalence);

    if (!unchanged) {
      // A stored contextual equivalence cannot be silently reassigned by a form
      // which did not expose/edit that equivalence.
      if (!equivalenceWasExposed && previous?.equivalence && parsed.value !== undefined
        && (unit !== previous.unit || basis !== (previous.basis ?? ''))) return failure('VALIDATION_ERROR');
      if (parsed.value === undefined) delete first.quantity;
      else first.quantity = {
        value: parsed.value,
        unit,
        ...(basis ? { basis } : {}),
        ...(equivalence ? { equivalence } : {}),
      };
    }
  }

  if (draft.composition !== undefined) {
    const composition = compositionFromForm(draft.composition);
    if (!composition.ok) return composition;
    first.composition = composition.value;
  }

  if (!validFoodEntry(first)) return failure('VALIDATION_ERROR');
  if (original) entries[0] = first;
  else entries.push(first);
  return { ok: true, value: entries };
}

export function formIntent<F, T>(fields: NutritionResult<F>, make: (fields: F) => NutritionIntent<T>): NutritionIntent<T> {
  return fields.ok ? make(fields.value) : { run: async () => fields };
}
